import { Fragment } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { db } from "@/lib/db-client";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fmtDateTime } from "@/lib/format";
import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/logs")({
  component: LogsAdmin,
});

const ACTIONS = [
  "auth.login", "auth.login_failed", "auth.logout",
  "ticket.create",
  "invoice.create",
  "client.create", "client.update", "client.delete",
  "material.create", "material.update", "material.delete",
  "pricing.update",
  "settings.update",
];

function LogsAdmin() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const load = async () => {
    setLoading(true);
    let q = db.from("audit_logs").select("*").order("created_at", { ascending: false }).limit(500);
    if (actionFilter !== "all") q = q.eq("action", actionFilter);
    const { data } = await q;
    setRows(data ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); }, [actionFilter]);

  const filtered = rows.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (r.user_email ?? "").toLowerCase().includes(s) ||
      (r.action ?? "").toLowerCase().includes(s) ||
      (r.entity ?? "").toLowerCase().includes(s) ||
      (r.entity_id ?? "").toLowerCase().includes(s) ||
      JSON.stringify(r.metadata ?? {}).toLowerCase().includes(s)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Journal d'activité</h1>
          <p className="text-sm text-muted-foreground">{filtered.length} événement{filtered.length > 1 ? "s" : ""} (max 500)</p>
        </div>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-1" /> Actualiser</Button>
      </div>

      <Card><CardContent className="p-4 flex flex-col sm:flex-row gap-2">
        <Input placeholder="Rechercher (email, action, entité, données…)" value={search} onChange={(e) => setSearch(e.target.value)} className="flex-1" />
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="sm:w-64"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les actions</SelectItem>
            {ACTIONS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
      </CardContent></Card>

      <Card><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Date</TableHead>
              <TableHead>Utilisateur</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Entité</TableHead>
              <TableHead>IP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Chargement…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Aucun événement</TableCell></TableRow>
            ) : filtered.map(r => {
              const isExp = !!expanded[r.id];
              return (
                <Fragment key={r.id}>
                  <TableRow className="cursor-pointer" onClick={() => setExpanded(s => ({ ...s, [r.id]: !s[r.id] }))}>
                    <TableCell>{isExp ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs">{fmtDateTime(r.created_at)}</TableCell>
                    <TableCell className="text-xs">
                      <div>{r.user_email ?? "—"}</div>
                      {r.is_admin && <Badge variant="secondary" className="mt-0.5 text-[10px]">admin</Badge>}
                    </TableCell>
                    <TableCell><Badge variant="outline">{r.action}</Badge></TableCell>
                    <TableCell className="text-xs">
                      {r.entity ? <>{r.entity}{r.entity_id ? <div className="text-muted-foreground">{String(r.entity_id).slice(0, 8)}…</div> : null}</> : "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.ip_address ?? "—"}</TableCell>
                  </TableRow>
                  {isExp && (
                    <TableRow>
                      <TableCell colSpan={6} className="bg-muted/30">
                        <div className="text-xs space-y-2 p-2">
                          <div><span className="font-semibold">User-agent : </span><span className="text-muted-foreground">{r.user_agent ?? "—"}</span></div>
                          <div className="font-semibold">Données :</div>
                          <pre className="bg-background border rounded p-2 overflow-x-auto text-[11px]">{JSON.stringify(r.metadata, null, 2)}</pre>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
